<script setup>
import gsap from '~/vendors/gsap'

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  image: {
    type: Object,
    default: null,
  },
  content: {
    type: Object,
    default: null,
  },
  callToAction: {
    type: Object,
    default: null,
  },
  callToActionGtmClass: {
    type: String,
    default: null,
  },
  index: {
    type: Number,
    default: -1,
  },
  isOpen: {
    type: Boolean,
    default: false,
  },
})

const isOpen = computed(() => props.isOpen)
const answer = ref(null)

const emit = defineEmits(['open', 'close'])

let openTimeline
let closeTimeline
function initTimelines() {
  openTimeline = gsap
    .timeline({ paused: true })
    .call(() => {
      answer.value.style.display = 'block'
    })
    .to(answer.value, {
      duration: 0.3,
      height: 'auto',
      ease: 'power2.out',
    })

  closeTimeline = gsap
    .timeline({ paused: true })
    .to(answer.value, {
      duration: 0.3,
      height: 0,
      ease: 'power2.out',
    })
    .call(() => {
      answer.value.style.display = 'none'
    })
}

watch(isOpen, (value) => {
  if (value) {
    openTimeline?.play(0)
  } else {
    closeTimeline?.play(0)
  }
})

function toggleItem() {
  if (props.isOpen) {
    emit('close', props.index)
  } else {
    emit('open', props.index)
  }
}

// Lifecycle
onMounted(() => {
  answer.value.style.display = 'none'
  initTimelines()
})
</script>

<template>
  <li class="faq-item" :class="{ 'faq-item--is-open': isOpen }">
    <button
      class="faq-item__header"
      @click="toggleItem()"
      :aria-controls="`FaqItem${index}`"
      :aria-expanded="isOpen"
    >
      <span class="text__eyebrow-small"></span>
      <span class="faq-item__header-title text__copy-medium">{{ title }}</span>
      <span class="faq-item__header-indicator"><Icon name="plus" /></span>
    </button>

    <div class="faq-item__answer" ref="answer" :id="`FaqItem${index}`">
      <div class="faq-item__answer-inner">
        <ContentfulRichText
          :json="content.json"
          :links="content.links"
          size="small"
        />

        <figure v-if="image">
          <img :src="image[0].src" alt=""/>
        </figure>

        <div class="faq-item__answer-cta" v-if="callToAction">
          <CallToAction
            :gtm-class="callToActionGtmClass"
            v-bind="callToAction"
            outline
            animate
            hide-size
          />
        </div>
      </div>
    </div>
  </li>
</template>

<style lang="scss">
// Vendors
@import 'styles/vendors/sass-mq/mq';

// Utils
@import 'styles/utils/variables';
@import 'styles/utils/timing-functions';

.faq-item {
  counter-increment: ol;

  &__header {
    padding: 19px 0px 28px;
    display: flex;
    align-items: center;
    text-align: left;
    width: 100%;
    color: $brand-white;
    position: relative;

    &:hover {
      .faq-item__header-indicator .svg {
        transform: rotate(-45deg) !important;
      }
    }

    &::before,
    &::after {
      width: 100%;
      height: 1px;
      background-color: rgba($brand-white, 0.2);
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      opacity: 0.2;
    }

    &::after {
      background: linear-gradient(#c80afb 0%, rgba(200, 10, 251, 0.63) 100%);
      opacity: 0;
      transition: opacity 0.3s $ease-out-quad;
    }

    .text__eyebrow-small {
      transition: transform 0.2s $ease-out-quad, opacity 0.2s $ease-out-quad;
      width: 42px;

      @include mq($from: 'l') using ($from) {
        width: 72px;
      }

      &::before {
        content: '0' counter(ol);
        color: rgba($brand-white, 0.5);
      }
    }

    &-title {
      opacity: 1;
      transition: transform 0.2s $ease-out-quad;
      color: $brand-white;
      text-align: left;
      padding-left: 10px;
      padding-right: 25px;
      width: 100%;
    }

    &-indicator {
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: $brand-purple-faded;
      border-radius: 50%;
      margin-left: auto;
      position: relative;
      overflow: hidden;
      flex-shrink: 0;

      @include mq($from: 'l') using ($from) {
        width: 44px;
        height: 44px;
      }

      &::before {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        content: '';
        background: linear-gradient(110.59deg, #c80afb 7.52%, #2919a9 153.34%);
        opacity: 0;
        transition: opacity 0.3s $ease-out-quad;
      }

      .svg {
        position: relative;
        z-index: 2;
        pointer-events: none;
        width: 14px;
        height: 14px;
        color: $brand-black;
        transition: transform 0.3s $ease-out-quad, color 0.3s $ease-out-quad;
      }
    }
  }

  &__answer {
    width: 100%;
    height: 0;
    overflow: hidden;

    figure,
    img {
      width: 100%;
      display: block;
    }
  }

  &__answer-inner {
    padding-bottom: 30px;
    > *:not(:last-child) {
      margin-bottom: 30px;
    }
  }

  &:nth-child(n + 10) .faq-item__header .text__eyebrow-small::before {
    content: counter(ol);
  }

  &.faq-item--is-open {
    .faq-item__header {
      &::after {
        opacity: 1;
      }

      .text__eyebrow-small {
        opacity: 0;
      }

      .text__eyebrow-small,
      &-title {
        transform: translateX(-42px);

        @include mq($from: 'l') using ($from) {
          transform: translateX(-72px);
        }
      }

      &-indicator {
        .svg {
          color: $brand-white;
          transform: rotate(45deg);
        }

        &::before {
          opacity: 1;
        }
      }
    }
  }
}
</style>
